import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo } from 'react'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useModalContext } from '@fourthwall/components'

import { PerkModal } from '../PerkModal/PerkModal'
import { FREE_TIER_ID } from '../consts'
import { useReorderPerkMutation } from '../hooks/useReorderPerkMutation'
import { useTierPerksQuery } from '../hooks/useTierPerksQuery'
import type { Perk } from '../types'

type Props = { tierId: number; children: ReactNode }

type PerksContextType = {
  isLoading: boolean
  perks: Perk[] | undefined
  addPerk: ((tierId: number) => void) | undefined
  editPerk: (perk: Perk) => void
  sortPerk: (id: number, oldIndex: number, newIndex: number) => void
}

export const PerksContext = createContext<PerksContextType | undefined>(undefined)

export const PerksProvider = ({ tierId, children }: Props) => {
  const modal = useModalContext()
  const { hasPermission } = useDashboardPermissions()

  const { tierPerksQuery } = useTierPerksQuery(
    [
      {
        tier: {
          id: tierId,
          type: tierId === FREE_TIER_ID ? 'free' : 'paid',
        },
      },
    ],
    {
      staleTime: Infinity,
      keepPreviousData: true,
    },
  )

  const { reorderPerkMutation } = useReorderPerkMutation()

  const isLoading = tierPerksQuery.isFetching || reorderPerkMutation.isLoading

  const handleAddExisitingPerk = useCallback(
    (perk: Perk, selectedTiers?: Record<string, boolean>) => {
      const getSelectedTiers = () => {
        const tiers: Record<string, boolean> = {}

        perk.tiers.forEach((selectedTier) => {
          tiers[selectedTier.id] = true
        })

        return { ...tiers, ...selectedTiers }
      }

      modal.close()

      // Required to reset Formik from previous modal
      setTimeout(() => {
        modal.open(PerkModal, { perk, selectedTiers: getSelectedTiers() })
      })
    },
    [modal],
  )

  const editPerk = useCallback(
    (perk: Perk) => {
      modal.open(PerkModal, { perk })
    },
    [modal],
  )

  const addPerk = useCallback(
    (tierId: number) => {
      modal.open(PerkModal, {
        selectedTiers: { [tierId]: true },
        onExistingPerkAdd: handleAddExisitingPerk,
      })
    },
    [handleAddExisitingPerk, modal],
  )

  const sortPerk = useCallback(
    (id: number, oldIndex: number, newIndex: number) => {
      reorderPerkMutation.mutate(
        [
          {
            id,
          },
          {
            tier: {
              id: tierId,
              type: tierId === FREE_TIER_ID ? 'free' : 'paid',
            },
          },
          {
            position: newIndex,
          },
        ],
        {
          onSuccess: () => {
            tierPerksQuery.refetch()
          },
        },
      )
    },
    [reorderPerkMutation, tierId, tierPerksQuery],
  )

  const value = useMemo(
    () => ({
      isLoading,
      perks: tierPerksQuery.data?.perks,
      addPerk: hasPermission('memberships.perksAndTiers.createPerk') ? addPerk : undefined,
      editPerk,
      sortPerk,
    }),
    [addPerk, editPerk, hasPermission, isLoading, sortPerk, tierPerksQuery.data?.perks],
  )

  return <PerksContext.Provider value={value}>{children}</PerksContext.Provider>
}
